import httpRequest from "../../ajax";
import { trackProfileUpdate, trackResumeUpload } from "../../utils/analytics";
import {
  GET_PROFILE,
  UPDATE_PROFILE,
  ERROR,
  SET_RESUME,
  GET_OTHER_USER,
  CLEAR_OTHER_USER,
  GET_ADMIN_REQUESTS,
  APPROVE_ADMIN_REQUEST,
  DECLINE_ADMIN_REQUEST,
} from "../actionTypes";

/**
 * @description set profile to local storage
 * @function persistUpdatedProfile
 * @param {object} data - profile
 * @returns {object} - returns the action
 */
// const persistUpdatedProfile = (data) => {
//   const rememberMe = localStorage.getItem("rememberMe");

//   if (rememberMe && rememberMe === "true") {
//     const prevProfile = JSON.parse(localStorage.getItem("userData"));
//     const newProfile = {
//       ...prevProfile,
//       ...data,
//       "0": {
//         ...prevProfile["0"],
//         ...(data["0"] || {})
//       }
//     };

//     if (data["0"]) {
//       Object.keys(data["0"]).forEach((key) => {
//         if (key in prevProfile) {
//           newProfile[key] = data["0"][key];
//         }
//       });
//     }

//     localStorage.setItem("userData", JSON.stringify(newProfile));
//     return newProfile;
//   } else {
//     const prevProfile = JSON.parse(sessionStorage.getItem("userData"));
//     const newProfile = {
//       ...prevProfile,
//       ...data,
//       "0": {
//         ...prevProfile["0"],
//         ...(data["0"] || {})
//       }
//     };

//     if (data["0"]) {
//       Object.keys(data["0"]).forEach((key) => {
//         if (key in prevProfile) {
//           newProfile[key] = data["0"][key];
//         }
//       });
//     }

//     sessionStorage.setItem("userData", JSON.stringify(newProfile));
//     return newProfile;
//   }
// };

const persistUpdatedProfile = (data) => {
  // Handle both array and object formats
  let profileData;
  if (Array.isArray(data) && data.length > 0) {
    profileData = data[0]; // Extract profile from array
  } else if (typeof data === "object" && data !== null) {
    profileData = data; // Use object directly
  } else {
    console.error("Invalid profile data format:", data);
    return null;
  }

  const storage = localStorage;

  const prevProfile = JSON.parse(storage.getItem("userData")) || {};

  // Merge the profiles, ensuring we keep the most recent data
  const newProfile = {
    ...prevProfile,
    ...profileData,
    // Ensure these calculated fields are always from the latest API response
    profileCompletionPercent: profileData.profileCompletionPercent,
    profileCompletionLevelPercent: profileData.profileCompletionLevelPercent,
  };

  // Preserve/derive phone verification status intelligently
  try {
    const prevVerified =
      typeof prevProfile.is_phone_verified !== "undefined"
        ? prevProfile.is_phone_verified
        : prevProfile.phone_verified;

    const responseVerified =
      typeof profileData.is_phone_verified !== "undefined"
        ? profileData.is_phone_verified
        : profileData.phone_verified;

    const prevPhone = prevProfile.phone;
    const responsePhone = profileData.phone;
    const prevPhoneExists =
      typeof prevPhone !== "undefined" &&
      prevPhone !== null &&
      String(prevPhone).trim() !== "";
    const phoneChanged =
      prevPhoneExists &&
      typeof responsePhone !== "undefined" &&
      responsePhone !== prevPhone;

    // If server does not explicitly provide verification flag, carry over previous value
    if (typeof responseVerified === "undefined") {
      // Only reset if a previously saved non-empty phone changed to a different one
      newProfile.is_phone_verified = phoneChanged ? 0 : prevVerified;
      newProfile.phone_verified = newProfile.is_phone_verified;
    } else {
      // Server provided a value; mirror to both keys
      newProfile.is_phone_verified = responseVerified;
      newProfile.phone_verified = responseVerified;
    }
  } catch (e) {
    // If anything goes wrong, do not block persistence
  }

  storage.setItem("userData", JSON.stringify(newProfile));
  console.log("Profile persisted:", newProfile);
  return newProfile;
};

/**
 * @description get profile action
 * @function getProfileAction
 * @param {function} dispatch - dispatch function
 * @returns {object} - returns the action
 */
export const getProfileAction = (dispatch) =>
  httpRequest("get", "/settings/profile")
    .then(({ data: { data } }) => {
      console.log("DATA FROM GET PROFILE", data);

      // Handle the array format from API
      const profileData = Array.isArray(data) ? data[0] : data;
      console.log("EXTRACTED PROFILE DATA", profileData);

      const newProfile = persistUpdatedProfile(data);
      console.log("NEW PROFILE AFTER GET PERSIST", newProfile);

      if (!newProfile) {
        throw new Error("Failed to process profile data");
      }

      const action = { type: GET_PROFILE, payload: newProfile };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => {
      console.error("Get profile error:", response);
      return {
        payload: response?.data?.message || "Failed to get profile",
        type: ERROR,
      };
    });

/**
 * @description update profile action
 * @function updateProfileAction
 * @param {object} requestData - request data
 * @param {function} dispatch - dispatch function
 * @returns {object} - returns the action
 */
export const updateProfileAction = (requestData, dispatch) =>
  httpRequest("post", "/settings/profile/update", requestData)
    .then(({ data: { data } }) => {
      console.log("RESPONSE DATA FROM UPDATE PROFILE", data);

      // Update API returns single object, not array
      const profileData = data;
      console.log("EXTRACTED UPDATE PROFILE DATA", profileData);

      const newProfile = persistUpdatedProfile(profileData);
      console.log("NEW PROFILE AFTER UPDATE PERSIST", newProfile);

      if (!newProfile) {
        throw new Error("Failed to process updated profile data");
      }

      const action = { type: UPDATE_PROFILE, payload: newProfile };
      dispatch(action);
      trackProfileUpdate(Object.keys(requestData || {}));
      return action;
    })
    .catch(({ response }) => {
      console.error("Update profile error:", response);
      return {
        payload: response?.data?.message || "Failed to update profile",
        type: ERROR,
      };
    });

/**
 * @description Force refresh profile from server (ignores cache)
 * @function refreshProfileAction
 * @param {function} dispatch - dispatch function
 * @returns {object} - returns the action
 */
export const refreshProfileAction = (dispatch) => {
  // Clear cached profile data before fetching
  const storage = localStorage;

  // Keep user auth data but clear profile-specific data
  const userData = JSON.parse(storage.getItem("userData")) || {};
  const authKeys = [
    "token",
    "id",
    "email",
    "username",
    "provider",
    "account_type",
    "user_type",
    "company",
    "isUserSubmittedMonthlySurvey",
  ]; // Keep these
  const cleanUserData = {};

  authKeys.forEach((key) => {
    if (userData[key]) {
      cleanUserData[key] = userData[key];
    }
  });

  storage.setItem("userData", JSON.stringify(cleanUserData));

  // Force fresh fetch
  return getProfileAction(dispatch);
};
// export const setResume = (dispatch, link) => {
//   dispatch({
//     type: SET_RESUME,
//     payload: link,
//   });
// };
export const setResume = (dispatch, resumeData) => {
  dispatch({
    type: SET_RESUME,
    payload: resumeData,
  });
  trackResumeUpload();
};

export const clearOtherUserAction = (dispatch) =>
  dispatch({ type: CLEAR_OTHER_USER });

export const getOtherUserAction = (dispatch, id) =>
  httpRequest("get", `/settings/profile/${id}/others`)
    .then(({ data: { data } }) => {
      const action = { type: GET_OTHER_USER, payload: data[0] };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message,
      type: ERROR,
    }));

export const getPendingAdminRequests = (dispatch) =>
  httpRequest("get", `/admin/requests/pending`)
    .then(({ data: { data } }) => {
      const action = { type: GET_ADMIN_REQUESTS, payload: data };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message,
      type: ERROR,
    }));

export const approveAdminRequests = (dispatch, id) =>
  httpRequest("post", `/admin/requests/${id}/approve`)
    .then(({ data: { data } }) => {
      const action = { type: APPROVE_ADMIN_REQUEST, payload: data };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message,
      type: ERROR,
    }));

export const declineAdminRequest = (dispatch, id) =>
  httpRequest("post", `/admin/requests/${id}/decline`)
    .then(({ data: { data } }) => {
      const action = { type: DECLINE_ADMIN_REQUEST, payload: data };
      dispatch(action);
      return action;
    })
    .catch(({ response }) => ({
      payload: response?.data?.message,
      type: ERROR,
    }));
